import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../lib/auth";
import { useConfig } from "../lib/config";
import { LogOut, Settings, LayoutDashboard, Plus, Users, Menu, X, CreditCard, Mail, Loader2 } from "lucide-react";
import AccountMenu from "./AccountMenu";
import api, { errMsg } from "../lib/api";
import { toast } from "../lib/notices";

/* NavLink applies aria-current="page" on the active route; the
   styling for that state lives on .brutal-btn-ghost[aria-current]
   in index.css, so "where am I" is answered without any local
   isActive plumbing. */
const navCls = "brutal-btn brutal-btn-ghost";

export default function Header() {
  const { user, logout } = useAuth();
  const { waitlistMode } = useConfig();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resendingVerify, setResendingVerify] = useState(false);
  const close = () => setMobileOpen(false);
  const doLogout = async () => { close(); await logout(); nav("/"); };

  // Every spend route (generate, dubs, launch kit, ...) now 403s an
  // unverified account, so this needs to be visible everywhere a spend
  // action might be triggered -- not just the Dashboard -- hence it lives
  // in Header, which every authenticated page renders.
  const resendVerify = async () => {
    setResendingVerify(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch (err) {
      toast.error(errMsg(err, "Could not send verification email"));
    } finally {
      setResendingVerify(false);
    }
  };

  const authedLinks = (
    <>
      <NavLink to="/dashboard" className={navCls} onClick={close} data-testid="nav-dashboard">
        <LayoutDashboard size={16} aria-hidden="true" /> Dashboard
      </NavLink>
      <NavLink to="/new" className={navCls} onClick={close} data-testid="nav-new-project">
        <Plus size={16} aria-hidden="true" /> New
      </NavLink>
      <NavLink to="/teams" className={navCls} onClick={close} data-testid="nav-teams">
        <Users size={16} aria-hidden="true" /> Teams
      </NavLink>
    </>
  );

  const publicLinks = (
    <>
      <NavLink to="/features" className={navCls} onClick={close}>Features</NavLink>
      <NavLink to="/pricing" className={navCls} onClick={close}>Pricing</NavLink>
      <NavLink to="/templates" className={navCls} onClick={close}>Templates</NavLink>
    </>
  );

  return (
    <>
    {/* First tab stop on every page. Lives here rather than in a layout so
        the 15 authed routes — which render <Header/> directly — get it too. */}
    <a href="#main" className="skip-link">Skip to content</a>
    <header
      className="sticky top-0 z-sticky bg-canvas/95 backdrop-blur border-b border-rule"
      data-testid="app-header"
    >
      <div className="max-w-wide mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
        {/* The CloudCraft link is a sibling, not a child, of the logo
            link: an <a> nested inside an <a> is invalid HTML and made
            the attribution unreachable by keyboard. */}
        <div className="flex items-center gap-3 shrink-0">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo.svg" alt="" aria-hidden="true" width="36" height="36" className="shrink-0" />
            <span className="font-display font-black text-lg leading-none tracking-tight">DemoCraft</span>
          </Link>
          <a
            href="https://www.cloudcraftlabs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden xl:inline font-mono text-2xs tracking-widest uppercase text-ink-muted hover:text-ink-accent transition-colors"
            data-testid="cloudcraft-link"
          >
            From CloudCraft Labs<span aria-hidden="true"> ↗</span>
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </div>

        <nav aria-label="Main" className="hidden lg:flex items-center gap-1">
          {user ? (
            <>
              {authedLinks}
              <span aria-hidden="true" className="w-px h-6 bg-rule-faint mx-2" />
              <AccountMenu email={user?.email} onLogout={doLogout} />
            </>
          ) : (
            <>
              {publicLinks}
              <NavLink to="/login" className={navCls} data-testid="nav-login">Login</NavLink>
              <Link to={waitlistMode ? "/waitlist" : "/register"} className="brutal-btn brutal-btn-sm ml-2" data-testid="nav-register">{waitlistMode ? "Join waitlist" : "Get Started"}</Link>
            </>
          )}
        </nav>

        <button
          type="button"
          className="lg:hidden brutal-btn brutal-btn-ghost"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
        </button>
      </div>

      {mobileOpen && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="lg:hidden border-t border-rule px-6 py-4 flex flex-col gap-1 bg-canvas"
        >
          {user ? (
            <>
              {authedLinks}
              {/* The account menu is a desktop affordance; on mobile
                  the panel is already a list, so its items are shown
                  inline rather than nested behind a second trigger. */}
              <NavLink to="/settings" className={navCls} onClick={close}>
                <Settings size={16} aria-hidden="true" /> Providers
              </NavLink>
              <NavLink to="/pricing" className={navCls} onClick={close}>
                <CreditCard size={16} aria-hidden="true" /> Plan &amp; billing
              </NavLink>
              <button type="button" onClick={doLogout} className="brutal-btn brutal-btn-secondary justify-start mt-2">
                <LogOut size={16} aria-hidden="true" /> Log out
              </button>
            </>
          ) : (
            <>
              {publicLinks}
              <NavLink to="/login" className={navCls} onClick={close}>Login</NavLink>
              <Link to={waitlistMode ? "/waitlist" : "/register"} className="brutal-btn justify-start mt-2" onClick={close}>{waitlistMode ? "Join waitlist" : "Get Started"}</Link>
            </>
          )}
        </nav>
      )}
    </header>
    {user && !user.email_verified && (
      <div
        className="bg-state-warning-surface border-b border-rule px-6 md:px-10 py-2 flex flex-wrap items-center justify-center gap-3 text-sm"
        role="status"
        data-testid="header-verify-banner"
      >
        <span className="inline-flex items-center gap-1.5">
          <Mail size={14} aria-hidden="true" /> Verify your email to generate videos.
        </span>
        <button
          type="button"
          onClick={resendVerify}
          disabled={resendingVerify}
          className="brutal-btn brutal-btn-ghost brutal-btn-sm inline-flex items-center gap-1"
          data-testid="header-resend-verify"
        >
          {resendingVerify ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : null}
          Resend verification email
        </button>
      </div>
    )}
    </>
  );
}
